import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {finalize, forkJoin} from 'rxjs';
import {MatIcon} from '@angular/material/icon';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {A11yModule} from '@angular/cdk/a11y';
import moment, {Moment} from 'moment';
import {BenchOverviewService} from '../../../services/bench-overview.service';
import {CompanyService} from '../../../services/company.service';
import {BenchFirm, SalesPersonDTO, SelectableUserDTO} from '../../../shared/model/bench-entry.model';
import {MessageService} from '../../../shared/components/message.service';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {CompanyBadgeComponent} from '../../../shared/components/company-badge/company-badge.component';
import {DateService} from '../../../services/date.service';

// Sales-&-Vertreter-Verwaltung (NP20-792): per-member CRUD (POST/PUT/DELETE), kein globales Speichern.
@Component({
  selector: 'app-sales-modal',
  imports: [
    FormsModule,
    TranslatePipe,
    MatIcon,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatAutocompleteModule,
    A11yModule,
    ModalComponent,
    CompanyBadgeComponent,
  ],
  templateUrl: './sales-modal.component.html',
  styleUrl: './sales-modal.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesModalComponent implements OnChanges {
  private readonly benchService = inject(BenchOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly dateService = inject(DateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly companyService = inject(CompanyService);

  @Input() visible = false;
  @Input() sales: SalesPersonDTO[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<SalesPersonDTO[]>();

  get firms(): BenchFirm[] {
    return this.companyService.companies().map(c => c.abbreviation);
  }
  companyName(abbreviation: string | null | undefined): string {
    return this.companyService.byAbbreviation(abbreviation ?? undefined)?.name ?? (abbreviation ?? '');
  }
  members: SalesPersonDTO[] = [];
  selectableUsers: SelectableUserDTO[] = [];
  isLoading = false;
  addSearch: Record<string, string> = {};
  pendingRemoval: SalesPersonDTO | null = null;

  private readonly savingIds = new Set<string>();
  private readonly momentCache = new Map<string, Moment>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reload();
    }
  }

  private reload(): void {
    this.isLoading = true;
    this.pendingRemoval = null;
    this.momentCache.clear();
    // NP20-894: Firmen als Teil des forkJoin laden (kein Race) und member.firm über
    // resolveAbbreviation normalisieren — analog bench-overview/meeting-mode. Sonst fallen
    // Mitglieder mit companyId-/abweichend geschriebener Firma aus ihrer Firmen-Sektion.
    forkJoin({
      companies: this.companyService.load(),
      members: this.benchService.getSales(),
      users: this.benchService.getSelectableUsers(),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({members, users}) => {
          this.members = members.map(m => ({
            ...m,
            firm: this.companyService.resolveAbbreviation(m.firm) ?? (m.firm ?? ''),
          }));
          this.selectableUsers = users;
          this.saved.emit(this.members); // keep the overview list/badge in sync
        },
        error: () => this.messageService.error('benchSales.loadError'),
      });
  }

  membersByFirm(firm: BenchFirm): SalesPersonDTO[] {
    return this.members.filter(m => m.firm === firm);
  }

  firmCount(firm: BenchFirm): number {
    return this.membersByFirm(firm).length;
  }

  get totalCount(): number {
    return this.members.length;
  }

  get representedCount(): number {
    return this.members.filter(m => this.isAbsent(m)).length;
  }

  /** Substitute options: any other member (the BE requires the substitute to be another member). */
  substituteOptions(member: SalesPersonDTO): SalesPersonDTO[] {
    return this.members.filter(m => m.userId !== member.userId);
  }

  memberLabel(member: SalesPersonDTO): string {
    return [member.abbreviation, member.fullName].filter(Boolean).join(' — ');
  }

  userLabel(user: SelectableUserDTO): string {
    return [user.abbreviation, user.fullName].filter(Boolean).join(' — ');
  }

  isAbsent(member: SalesPersonDTO): boolean {
    return this.dateService.isSalesMemberAbsent(member);
  }

  hasInvalidRange(member: SalesPersonDTO): boolean {
    return !!member.absenceFrom && !!member.absenceUntil && member.absenceFrom > member.absenceUntil;
  }

  isSaving(member: SalesPersonDTO): boolean {
    return this.savingIds.has(member.id);
  }

  // --- add a member (via user picker) ---------------------------------------

  /** Selectable users filtered by the per-firm search term (matches Kürzel or full name). */
  filteredUsers(firm: BenchFirm): SelectableUserDTO[] {
    const term = (this.addSearch[firm] ?? '').trim().toLowerCase();
    if (!term) return this.selectableUsers;
    return this.selectableUsers.filter(u =>
      u.abbreviation.toLowerCase().includes(term) || u.fullName.toLowerCase().includes(term));
  }

  onAddSelected(firm: BenchFirm, userId: string, input?: HTMLInputElement): void {
    this.addSearch[firm] = '';
    // Autocomplete schreibt die userId ins Feld; war es vorher leer, sieht ngModel keine Änderung und räumt sie nicht weg.
    if (input) input.value = '';
    this.addMember(firm, userId);
  }

  addMember(firm: BenchFirm, userId: string): void {
    if (!userId) return;
    this.benchService.addSalesPerson({userId, firm})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: (err: unknown) => this.handleError(err, 'add'),
      });
  }

  // --- inline auto-save per member ------------------------------------------

  persist(member: SalesPersonDTO): void {
    if (this.hasInvalidRange(member)) {
      this.messageService.error('benchSales.invalidRange');
      return;
    }
    // BE rule (NP20-792): an absence requires a substitute. Enforce it client-side
    // with a clear message instead of letting the PUT 400.
    const hasAbsence = !!member.absenceFrom || !!member.absenceUntil || !!member.absenceReason?.trim();
    if (hasAbsence && !member.substituteUserId) {
      this.messageService.error('benchSales.absenceNeedsSubstitute');
      return;
    }

    this.savingIds.add(member.id);
    this.benchService.updateSalesPerson(member.id, {
      firm: member.firm,
      absenceFrom: member.absenceFrom,
      absenceUntil: member.absenceUntil,
      absenceReason: member.absenceReason,
      substituteUserId: member.substituteUserId,
    })
      .pipe(
        finalize(() => {
          this.savingIds.delete(member.id);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: updated => {
          // Immutable-Update (statt Object.assign auf die Referenz) — OnPush-freundlich
          this.members = this.members.map(m => m.id === updated.id ? updated : m);
          this.saved.emit(this.members);
        },
        error: (err: unknown) => this.handleError(err, 'update'),
      });
  }

  // --- remove a member ------------------------------------------------------

  requestRemove(member: SalesPersonDTO): void {
    this.pendingRemoval = member;
  }

  cancelRemove(): void {
    this.pendingRemoval = null;
  }

  get pendingRemovalLabel(): string {
    return this.pendingRemoval ? this.memberLabel(this.pendingRemoval) : '';
  }

  confirmRemove(): void {
    const member = this.pendingRemoval;
    this.pendingRemoval = null;
    if (!member) return;
    this.benchService.deleteSalesPerson(member.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: (err: unknown) => this.handleError(err, 'delete', member),
      });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // --- date <-> moment ------------------------------------------------------

  toMoment(value: string | null): Moment | null {
    if (!value) return null;
    let m = this.momentCache.get(value);
    if (!m) {
      m = moment(value, 'YYYY-MM-DD');
      this.momentCache.set(value, m);
    }
    return m;
  }

  toIso(value: Moment | null): string | null {
    return this.dateService.toLocalDateString(value);
  }

  private substituteHoldersOf(member: SalesPersonDTO): SalesPersonDTO[] {
    return this.members.filter(m => m.userId !== member.userId && m.substituteUserId === member.userId);
  }

  private labelForId(id: string): string | null {
    const member = this.members.find(m => m.userId === id || m.id === id);
    if (member) return this.memberLabel(member);
    const user = this.selectableUsers.find(u => u.id === id);
    return user ? this.userLabel(user) : null;
  }

  private resolveIdsInDetail(detail: string): string {
    const ids = new Set<string>();
    this.members.forEach(m => {
      if (m.userId) ids.add(m.userId);
      if (m.id) ids.add(m.id);
    });
    this.selectableUsers.forEach(u => {
      if (u.id) ids.add(u.id);
    });
    return [...ids]
      .sort((a, b) => b.length - a.length)
      .reduce((text, id) => text.includes(id) ? text.split(id).join(this.labelForId(id) ?? id) : text, detail);
  }

  private handleError(err: unknown, op: 'add' | 'update' | 'delete', member?: SalesPersonDTO): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const rawDetail = err instanceof HttpErrorResponse ? (err.error?.detail ?? err.error?.message ?? '') : '';
    const detail = this.resolveIdsInDetail(rawDetail);
    let key = 'benchSales.saveError';
    let params: Record<string, unknown> | undefined;
    if (op === 'add' && status === 409) key = 'benchSales.error.alreadyMember';
    else if (op === 'add' && status === 400) key = 'benchSales.error.addRejected';
    else if (op === 'delete' && status === 409) {
      key = 'benchSales.error.inUse';
      const holders = member ? this.substituteHoldersOf(member) : [];
      if (member && holders.length) {
        key = 'benchSales.error.inUseSubstitute';
        params = {name: this.memberLabel(member), holders: holders.map(h => this.memberLabel(h)).join(', ')};
      }
    }

    // NP20-850: Der Grund aus der Antwort gewinnt immer — ein 400 beim Hinzufuegen wurde vorher
    // pauschal als "Benutzer wurde nicht gefunden" gemeldet, auch wenn es an etwas anderem lag.
    if (detail && params) {
      this.messageService.errorWithDetail(key, detail, undefined, params);
    } else if (detail) {
      this.messageService.errorWithDetail(key, detail);
    } else if (params) {
      this.messageService.error(key, undefined, params);
    } else {
      this.messageService.error(key);
    }
    // A failed update/delete may leave the row out of sync — pull a fresh list.
    if (op !== 'add') this.reload();
  }
}
